import type { KeyboardEvent } from 'react';
import { useCallback, useState } from 'react';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';

export interface AddNoteComposerProps {
  busy?: boolean;
  placeholder?: string;
  onSave: (body: string) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}

/** Enter commits, and so does Cmd/Ctrl+Enter; Shift+Enter is the line break. */
function isSaveKey(event: KeyboardEvent<HTMLDivElement>): boolean {
  if (event.key !== 'Enter') return false;
  return !event.shiftKey || event.metaKey || event.ctrlKey;
}

/**
 * The note composer, one component for both places that take a note
 * (GDN-3380): the row menu, where it replaces the menu's own items, and the
 * workbench Notes card, where it sits under the list.
 *
 * It behaves like a chat input: one line tall until the text needs more,
 * Enter sends, Shift+Enter breaks the line, and the send control is the
 * return key at the end of the field rather than a button row. It draws no
 * padding and no helper text of its own — each surface supplies its inset,
 * and the field already says what it is for.
 *
 * Empty and whitespace-only saves are refused up front — Enter does nothing
 * and the return key stays disabled — rather than failing after the fact. A
 * note is free text with no other validation; the only wrong note is one that
 * says nothing.
 */
export function AddNoteComposer({
  busy,
  placeholder = 'Add a note',
  onSave,
  onCancel,
  autoFocus,
}: AddNoteComposerProps) {
  const [body, setBody] = useState('');
  const trimmed = body.trim();
  const canSave = Boolean(trimmed) && !busy;

  const save = useCallback(() => {
    if (!trimmed) return;
    onSave(trimmed);
    setBody('');
  }, [onSave, trimmed]);

  return (
    <Box>
      <TextField
        autoFocus={autoFocus}
        fullWidth
        multiline
        minRows={1}
        maxRows={8}
        size="small"
        value={body}
        placeholder={placeholder}
        onChange={event => setBody(event.target.value)}
        onKeyDown={event => {
          /* Stopped before anything else: this composer renders INSIDE a MUI
             Menu, whose MenuList treats Arrow/Home/End on any descendant as
             list navigation and preventDefaults them — which would take the
             caret out of a multiline field mid-sentence. */
          event.stopPropagation();
          if (isSaveKey(event)) {
            event.preventDefault();
            if (canSave) save();
          }
          /* Escape hands the surface back unchanged, where there is one. */
          if (event.key === 'Escape' && onCancel) onCancel();
        }}
        slotProps={{
          htmlInput: { 'aria-label': placeholder, 'aria-keyshortcuts': 'Enter' },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Save note"
                  size="small"
                  edge="end"
                  disabled={!canSave}
                  onClick={save}
                >
                  {busy ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <KeyboardReturnIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        sx={{
          /* A paper field even on the stage's grey ground: an outlined input is
             transparent by default, and a grey box on grey reads as disabled.
             The box is pinned so focus changes nothing but the outline's
             border: explicit padding, and everything centred on the block —
             the adornment is a zero-height flex item, so `center` is what puts
             the return key on the text's centre line for one line and on the
             block's for many. */
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.paper',
            alignItems: 'center',
            padding: '8.5px 14px',
          },
          '& .MuiInputBase-input': { fontSize: '0.8125rem', lineHeight: 1.4 },
        }}
      />
      {onCancel && (
        <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: 0.5 }}>
          <Button color="inherit" onClick={onCancel} size="small" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        </Stack>
      )}
    </Box>
  );
}
